import React from "react";
import { Link } from "react-router-dom";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import CodeIcon from "@mui/icons-material/Code";
import BookIcon from "@mui/icons-material/Book";
import DatabaseIcon from "@mui/icons-material/Storage";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import EventNoteIcon from "@mui/icons-material/EventNote"; // Icon for Book a Demo
import ButtonWrapper from "../components/ButtonWrapper";
import { eButtonVariant } from "../types/interfaces";

const HomePage: React.FC = () => {
  return (
    <div className="text-center p-6">
      {/* Hero Section */}
      <div className="bg-gray-100 p-12 mb-8 rounded-lg shadow-lg">
        <h1 className="text-4xl font-bold mb-4">Welcome to sustamize</h1>
        <p className="text-lg mb-6">
          Explore our comprehensive databases and resources for precise Product
          Carbon Footprint (PCF) calculation and optimization to reduce Scope 3
          emissions and achieve environmental sustainability. Learn how to get
          started, explore our API, and find the data you need.
        </p>
        <Link to="/database-search">
          <ButtonWrapper variant={eButtonVariant.text}>
            Explore Databases
          </ButtonWrapper>
        </Link>
      </div>

      {/* Features Section */}
      <div className="p-6">
        <h2 className="text-2xl font-semibold mb-8">What We Offer</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Feature 1: Getting Started */}
          <Link
            to="/knowledge-hub/getting-started/commercials"
            className="block bg-white p-6 shadow-md rounded-lg hover:shadow-xl transform transition-all duration-200"
          >
            <div className="flex flex-col items-center">
              <RocketLaunchIcon className="text-4xl mb-2 text-green-600" />
              <h3 className="text-xl font-semibold mb-2">Getting Started</h3>
              <p className="mb-4">
                Learn how to create an account, explore datasets, and use our
                data effectively.
              </p>
              <span className="text-blue-600 hover:underline">
                Get Started →
              </span>
            </div>
          </Link>

          {/* Feature 2: API Web Reference */}
          <Link
            to="/api-web-reference/overview"
            className="block bg-white p-6 shadow-md rounded-lg hover:shadow-xl transform transition-all duration-200"
          >
            <div className="flex flex-col items-center">
              <CodeIcon className="text-4xl mb-2 text-green-600" />
              <h3 className="text-xl font-semibold mb-2">API Web Reference</h3>
              <p className="mb-4">
                Access our powerful API for integrating sustainability data into
                your applications.
              </p>
              <span className="text-blue-600 hover:underline">
                Explore API Docs →
              </span>
            </div>
          </Link>

          {/* Database Overview */}
          <Link
            to="/knowledge-hub/database-overview/overview"
            className="block bg-white p-6 shadow-md rounded-lg hover:shadow-xl transform transition-all duration-200"
          >
            <div className="flex flex-col items-center">
              <DatabaseIcon className="text-4xl mb-2 text-green-600" />
              <h3 className="text-xl font-semibold mb-2">Database Overview</h3>
              <p className="mb-4">
                Get a detailed overview of the databases currently available.
              </p>
              <span className="text-blue-600 hover:underline">
                Visit Knowledge Hub →
              </span>
            </div>
          </Link>
        </div>
      </div>

      {/* Additional Resources Section */}
      <div className="p-6">
        <h2 className="text-2xl font-semibold mb-8">Explore More</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Data Quality */}
          <Link
            to="/knowledge-hub/data-quality"
            className="block bg-white p-6 shadow-md rounded-lg hover:shadow-xl transform transition-all duration-200"
          >
            <div className="flex flex-col items-center">
              <CheckCircleIcon className="text-4xl mb-2 text-green-600" />
              <h3 className="text-xl font-semibold mb-2">Data Quality</h3>
              <p className="mb-4">
                Learn about our data quality processes and how we ensure the
                accuracy of our datasets.
              </p>
              <span className="text-blue-600 hover:underline">
                Learn About Data Quality →
              </span>
            </div>
          </Link>

          {/* Latest Releases */}
          <Link
            to="/knowledge-hub/releases"
            className="block bg-white p-6 shadow-md rounded-lg hover:shadow-xl transform transition-all duration-200"
          >
            <div className="flex flex-col items-center">
              <NewReleasesIcon className="text-4xl mb-2 text-green-600" />
              <h3 className="text-xl font-semibold mb-2">Latest Releases</h3>
              <p className="mb-4">
                Stay updated with the latest releases and updates to our
                datasets and new features.
              </p>
              <span className="text-blue-600 hover:underline">
                View Latest Releases →
              </span>
            </div>
          </Link>

          {/* New Block for Book a Demo */}
          <Link
            to="https://meetings-eu1.hubspot.com/nicolas-carl" // External link for demo booking
            target="_blank"
            rel="noopener noreferrer"
            className="block bg-white p-6 shadow-md rounded-lg hover:shadow-xl transform transition-all duration-200"
          >
            <div className="flex flex-col items-center">
              <EventNoteIcon className="text-4xl mb-2 text-green-600" />{" "}
              {/* Icon for Book a Demo */}
              <h3 className="text-xl font-semibold mb-2">Book a Demo</h3>
              <p className="mb-4">
                Schedule a personalized demo to discuss the best utilization of
                our data for you.
              </p>
              <span className="text-blue-600 hover:underline">Book Now →</span>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
